import { useState } from "react";
import { Check, Mail, Instagram, MapPin } from "lucide-react";
import { toast } from "sonner";
import { useSEO } from "@/hooks/useSEO";
import { Reveal } from "@/components/Reveal";
import { submitContact } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const TOPICS = [
  { value: "collaboration", label: "Collaboration" },
  { value: "exhibition", label: "Exhibition or curatorial opportunity" },
  { value: "consulting", label: "1:1 artist consulting" },
  { value: "grant-press", label: "Grant panel / press materials" },
  { value: "general", label: "Something else" },
];

export default function ContactPage() {
  useSEO(
    "Contact & Collaboration · The House of Ego · Seyoun Taylor",
    "Reach Seyoun Taylor of The House of Ego for artwork inquiries, commissions, exhibitions, consulting, and collaborations. Based in Dallas–Fort Worth, working everywhere."
  );
  const [form, setForm] = useState({ name: "", email: "", topic: "", message: "" });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.topic) {
      toast.error("Please choose a topic.");
      return;
    }
    setSending(true);
    try {
      await submitContact(form);
      setSent(true);
      toast.success("Message sent. Thank you for reaching out.");
    } catch {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setSending(false);
    }
  };

  return (
    <main id="main-content" data-testid="contact-page">
      <div className="mx-auto max-w-7xl px-6 md:px-10 pt-40 md:pt-52 pb-24 md:pb-32 grid gap-16 lg:grid-cols-[1fr_1.2fr]">
        <div>
          <Reveal>
            <p className="eyebrow">Contact &amp; Collaboration</p>
            <h1 className="mt-6 font-serif text-5xl md:text-7xl tracking-tight text-ink leading-[1.05]">
              Let’s connect.
            </h1>
            <p className="mt-8 max-w-md text-lg leading-relaxed text-neutral-600">
              Commissions, exhibitions, consulting, press, or a conversation about what the work stirs
              in you. Every message reaches Seyoun directly.
            </p>
          </Reveal>
          <Reveal delay={0.15}>
            <div className="mt-12 space-y-5 text-sm">
              <p className="flex items-center gap-3">
                <Mail size={16} className="text-flame" />
                <a
                  href="mailto:seyountaylor60@gmail.com"
                  data-testid="contact-email-link"
                  className="link-underline text-ink"
                >
                  seyountaylor60@gmail.com
                </a>
              </p>
              <p className="flex items-center gap-3">
                <Instagram size={16} className="text-flame" />
                <a
                  href="https://instagram.com/seyountheeartist"
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="contact-instagram"
                  className="link-underline text-ink"
                >
                  @seyountheeartist
                </a>
              </p>
              <p className="flex items-center gap-3">
                <MapPin size={16} className="text-flame" />
                <span>Dallas–Fort Worth, Texas · Shipping &amp; traveling everywhere</span>
              </p>
            </div>
          </Reveal>
        </div>

        <Reveal delay={0.15}>
          <div className="bg-white border border-neutral-200 p-8 md:p-12" data-testid="contact-panel">
            {sent ? (
              <div className="text-center py-14" data-testid="contact-success">
                <span className="inline-flex h-12 w-12 items-center justify-center rounded-full bg-royal-soft text-royal">
                  <Check size={22} />
                </span>
                <h2 className="mt-5 font-serif text-3xl text-ink">Message received.</h2>
                <p className="mt-3 text-sm text-neutral-600 max-w-sm mx-auto">
                  Thank you for reaching out. Seyoun reads and replies to every message personally.
                </p>
              </div>
            ) : (
              <form onSubmit={onSubmit} className="space-y-7" data-testid="contact-form">
                <div className="grid sm:grid-cols-2 gap-7">
                  <div>
                    <Label htmlFor="ct-name" className="text-xs uppercase tracking-[0.2em] text-neutral-500">
                      Name
                    </Label>
                    <Input
                      id="ct-name"
                      data-testid="contact-name-input"
                      required
                      value={form.name}
                      onChange={(e) => setForm({ ...form, name: e.target.value })}
                      className="field"
                      placeholder="Your name"
                    />
                  </div>
                  <div>
                    <Label htmlFor="ct-email" className="text-xs uppercase tracking-[0.2em] text-neutral-500">
                      Email
                    </Label>
                    <Input
                      id="ct-email"
                      type="email"
                      data-testid="contact-email-input"
                      required
                      value={form.email}
                      onChange={(e) => setForm({ ...form, email: e.target.value })}
                      className="field"
                      placeholder="you@example.com"
                    />
                  </div>
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-[0.2em] text-neutral-500">
                    I’m reaching out about
                  </Label>
                  <Select value={form.topic} onValueChange={(v) => setForm({ ...form, topic: v })}>
                    <SelectTrigger data-testid="contact-topic-select" className="field mt-1 flex justify-between">
                      <SelectValue placeholder="Choose one" />
                    </SelectTrigger>
                    <SelectContent>
                      {TOPICS.map((t) => (
                        <SelectItem key={t.value} value={t.value} data-testid={`contact-topic-${t.value}`}>
                          {t.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label htmlFor="ct-message" className="text-xs uppercase tracking-[0.2em] text-neutral-500">
                    Message
                  </Label>
                  <Textarea
                    id="ct-message"
                    data-testid="contact-message-input"
                    required
                    rows={5}
                    value={form.message}
                    onChange={(e) => setForm({ ...form, message: e.target.value })}
                    className="field resize-none"
                    placeholder="Tell Seyoun what you have in mind…"
                  />
                </div>
                <button
                  type="submit"
                  data-testid="contact-submit-button"
                  disabled={sending}
                  className="w-full bg-royal text-white py-4 text-sm tracking-wide hover:bg-royal-deep transition-colors disabled:opacity-60"
                >
                  {sending ? "Sending…" : "Send message"}
                </button>
              </form>
            )}
          </div>
        </Reveal>
      </div>
    </main>
  );
}
